/**
 * Attack Detection Service
 * Detects and analyzes various types of security attacks
 * Now saves to Firebase for persistence
 */

import FirebaseSecurityService from './firebaseSecurityService';

export interface AttackPattern {
    type: 'brute_force' | 'ddos' | 'sql_injection' | 'xss' | 'path_traversal' | 'suspicious_activity';
    severity: 'low' | 'medium' | 'high' | 'critical';
    description: string;
    indicators: string[];
}

export interface DetectedAttack {
    id: string;
    ip: string;
    type: AttackPattern['type'];
    severity: AttackPattern['severity'];
    timestamp: Date;
    details: {
        requestCount?: number;
        timeWindow?: number; // minutes
        endpoint?: string;
        payload?: string;
        userAgent?: string;
        method?: string;
    };
    blocked: boolean;
    autoBlocked: boolean;
}

export interface IPActivity {
    ip: string;
    requestCount: number;
    failedLoginAttempts: number;
    suspiciousRequests: number;
    firstSeen: Date;
    lastSeen: Date;
    endpoints: string[];
    userAgents: string[];
}

class AttackDetectionService {
    private static ipActivity: Map<string, IPActivity> = new Map();
    private static detectedAttacks: DetectedAttack[] = [];
    private static blockedIPs: Set<string> = new Set();

    // Attack patterns
    private static readonly ATTACK_PATTERNS: AttackPattern[] = [
        {
            type: 'brute_force',
            severity: 'high',
            description: 'Multiple failed login attempts',
            indicators: ['failed_login', 'rapid_requests', 'auth_endpoint']
        },
        {
            type: 'ddos',
            severity: 'critical',
            description: 'Distributed Denial of Service attack',
            indicators: ['high_request_rate', 'multiple_ips', 'resource_exhaustion']
        },
        {
            type: 'sql_injection',
            severity: 'critical',
            description: 'SQL injection attempt',
            indicators: ['sql_keywords', 'union_select', 'drop_table', 'or_1=1']
        },
        {
            type: 'xss',
            severity: 'high',
            description: 'Cross-Site Scripting attempt',
            indicators: ['script_tag', 'javascript:', 'onerror=', 'onclick=']
        },
        {
            type: 'path_traversal',
            severity: 'high',
            description: 'Path traversal attempt',
            indicators: ['../..', '..\\..', '/etc/passwd', 'windows\\system32']
        },
        {
            type: 'suspicious_activity',
            severity: 'medium',
            description: 'Suspicious behavior detected',
            indicators: ['unusual_pattern', 'bot_behavior', 'scanning']
        }
    ];

    // Thresholds for detection
    private static readonly THRESHOLDS = {
        BRUTE_FORCE_ATTEMPTS: 5,
        BRUTE_FORCE_WINDOW: 5, // minutes
        DDOS_REQUESTS_PER_MINUTE: 100,
        DDOS_WINDOW: 1, // minute
        SUSPICIOUS_REQUESTS_THRESHOLD: 10,
        FAILED_LOGIN_THRESHOLD: 3
    };

    /**
     * Track a request from an IP
     */
    static trackRequest(ip: string, endpoint: string, userAgent: string, method: string = 'GET'): void {
        const now = new Date();

        if (!this.ipActivity.has(ip)) {
            this.ipActivity.set(ip, {
                ip,
                requestCount: 0,
                failedLoginAttempts: 0,
                suspiciousRequests: 0,
                firstSeen: now,
                lastSeen: now,
                endpoints: [],
                userAgents: []
            });
        }

        const activity = this.ipActivity.get(ip)!;
        activity.requestCount++;
        activity.lastSeen = now;

        if (!activity.endpoints.includes(endpoint)) {
            activity.endpoints.push(endpoint);
        }

        if (!activity.userAgents.includes(userAgent)) {
            activity.userAgents.push(userAgent);
        }

        // Check for attacks
        this.detectAttacks(ip, endpoint, method, userAgent);
    }

    /**
     * Track a failed login attempt
     */
    static trackFailedLogin(ip: string, email: string): void {
        const activity = this.ipActivity.get(ip);
        if (activity) {
            activity.failedLoginAttempts++;
        }

        // Check for brute force
        if (activity && activity.failedLoginAttempts >= this.THRESHOLDS.BRUTE_FORCE_ATTEMPTS) {
            this.reportAttack({
                id: `attack-${Date.now()}-${ip}`,
                ip,
                type: 'brute_force',
                severity: 'high',
                timestamp: new Date(),
                details: {
                    requestCount: activity.failedLoginAttempts,
                    timeWindow: this.THRESHOLDS.BRUTE_FORCE_WINDOW,
                    endpoint: '/login',
                    payload: `Failed login attempts for: ${email}`
                },
                blocked: false,
                autoBlocked: false
            });
        }
    }

    /**
     * Detect various types of attacks
     */
    private static detectAttacks(ip: string, endpoint: string, method: string, userAgent: string): void {
        const activity = this.ipActivity.get(ip)!;

        // DDoS Detection
        const recentRequests = this.getRecentRequestCount(ip, this.THRESHOLDS.DDOS_WINDOW);
        if (recentRequests > this.THRESHOLDS.DDOS_REQUESTS_PER_MINUTE) {
            this.reportAttack({
                id: `attack-${Date.now()}-${ip}`,
                ip,
                type: 'ddos',
                severity: 'critical',
                timestamp: new Date(),
                details: {
                    requestCount: recentRequests,
                    timeWindow: this.THRESHOLDS.DDOS_WINDOW,
                    endpoint,
                    userAgent
                },
                blocked: false,
                autoBlocked: false
            });
        }

        // SQL Injection Detection
        if (this.detectSQLInjection(endpoint)) {
            this.reportAttack({
                id: `attack-${Date.now()}-${ip}`,
                ip,
                type: 'sql_injection',
                severity: 'critical',
                timestamp: new Date(),
                details: {
                    endpoint,
                    payload: endpoint,
                    method,
                    userAgent
                },
                blocked: false,
                autoBlocked: false
            });
        }

        // XSS Detection
        if (this.detectXSS(endpoint)) {
            this.reportAttack({
                id: `attack-${Date.now()}-${ip}`,
                ip,
                type: 'xss',
                severity: 'high',
                timestamp: new Date(),
                details: {
                    endpoint,
                    payload: endpoint,
                    method,
                    userAgent
                },
                blocked: false,
                autoBlocked: false
            });
        }

        // Path Traversal Detection
        if (this.detectPathTraversal(endpoint)) {
            this.reportAttack({
                id: `attack-${Date.now()}-${ip}`,
                ip,
                type: 'path_traversal',
                severity: 'high',
                timestamp: new Date(),
                details: {
                    endpoint,
                    payload: endpoint,
                    method,
                    userAgent
                },
                blocked: false,
                autoBlocked: false
            });
        }

        // Suspicious Activity Detection
        if (activity.endpoints.length > 20 || activity.userAgents.length > 5) {
            activity.suspiciousRequests++;
            if (activity.suspiciousRequests >= this.THRESHOLDS.SUSPICIOUS_REQUESTS_THRESHOLD) {
                this.reportAttack({
                    id: `attack-${Date.now()}-${ip}`,
                    ip,
                    type: 'suspicious_activity',
                    severity: 'medium',
                    timestamp: new Date(),
                    details: {
                        requestCount: activity.requestCount,
                        endpoint: 'Multiple endpoints',
                        userAgent: `${activity.userAgents.length} different user agents`
                    },
                    blocked: false,
                    autoBlocked: false
                });
            }
        }
    }

    /**
     * Detect SQL injection patterns
     */
    private static detectSQLInjection(input: string): boolean {
        const sqlPatterns = [
            /(\bunion\b.*\bselect\b)/i,
            /(\bselect\b.*\bfrom\b)/i,
            /(\bdrop\b.*\btable\b)/i,
            /(\binsert\b.*\binto\b)/i,
            /(\bdelete\b.*\bfrom\b)/i,
            /(\bupdate\b.*\bset\b)/i,
            /(--|\#|\/\*)/,
            /(\bor\b\s*['"]?\d+['"]?\s*=\s*['"]?\d+)/i,
            /(\band\b\s*['"]?\d+['"]?\s*=\s*['"]?\d+)/i,
            /(exec\s*\()/i,
            /(execute\s*\()/i
        ];

        return sqlPatterns.some(pattern => pattern.test(input));
    }

    /**
     * Detect XSS patterns
     */
    private static detectXSS(input: string): boolean {
        const xssPatterns = [
            /<script[^>]*>.*?<\/script>/i,
            /javascript:/i,
            /on\w+\s*=/i,
            /<iframe[^>]*>/i,
            /<object[^>]*>/i,
            /<embed[^>]*>/i,
            /eval\s*\(/i,
            /expression\s*\(/i
        ];

        return xssPatterns.some(pattern => pattern.test(input));
    }

    /**
     * Detect path traversal patterns
     */
    private static detectPathTraversal(input: string): boolean {
        const pathPatterns = [
            /\.\.[\/\\]/,
            /\/etc\/passwd/i,
            /\/windows\/system32/i,
            /\.\.%2f/i,
            /\.\.%5c/i,
            /%2e%2e[\/\\]/i
        ];

        return pathPatterns.some(pattern => pattern.test(input));
    }

    /**
     * Get recent request count for an IP
     */
    private static getRecentRequestCount(ip: string, windowMinutes: number): number {
        const activity = this.ipActivity.get(ip);
        if (!activity) return 0;

        const now = new Date();
        const windowMs = windowMinutes * 60 * 1000;
        const timeSinceLastRequest = now.getTime() - activity.lastSeen.getTime();

        if (timeSinceLastRequest > windowMs) {
            return 0;
        }

        return activity.requestCount;
    }

    /**
     * Report an attack
     */
    private static async reportAttack(attack: DetectedAttack): Promise<void> {
        // Check if this attack was already reported recently
        const recentAttack = this.detectedAttacks.find(
            a => a.ip === attack.ip &&
                a.type === attack.type &&
                (Date.now() - a.timestamp.getTime()) < 60000 // within 1 minute
        );

        if (recentAttack) {
            return; // Don't report duplicate attacks
        }

        this.detectedAttacks.push(attack);
        console.warn(`🚨 Attack detected: ${attack.type} from ${attack.ip}`);

        // Save to Firebase
        await FirebaseSecurityService.addSecurityEvent(
            attack.type,
            attack.severity,
            attack.ip,
            `${attack.type.replace(/_/g, ' ')} detected from ${attack.ip}`,
            JSON.stringify(attack.details)
        );

        // Log attack to Firebase
        await FirebaseSecurityService.logAttack(
            attack.type,
            attack.ip,
            attack.details.payload,
            attack.details.endpoint,
            attack.blocked,
            attack.autoBlocked
        );

        // Auto-block critical attacks
        if (attack.severity === 'critical' || attack.type === 'brute_force') {
            await this.autoBlockIP(attack.ip, attack.type, attack.severity);
            attack.autoBlocked = true;
            attack.blocked = true;
        }
    }

    /**
     * Automatically block an IP
     */
    private static async autoBlockIP(ip: string, reason: string, severity: string): Promise<void> {
        if (!this.blockedIPs.has(ip)) {
            this.blockedIPs.add(ip);

            // Block in Firebase
            const severityLevel = (severity === 'critical' || severity === 'high') ? 'critical' : 'high';
            await FirebaseSecurityService.blockIP(
                ip,
                `Auto-blocked: ${reason}`,
                severityLevel as 'low' | 'medium' | 'high' | 'critical',
                'system'
            );

            console.warn(`🚫 Auto-blocked IP: ${ip} (Reason: ${reason}, Severity: ${severity})`);
        }
    }

    /**
     * Check if an IP is blocked
     */
    static isIPBlocked(ip: string): boolean {
        return this.blockedIPs.has(ip);
    }

    /**
     * Manually block an IP
     */
    static blockIP(ip: string): void {
        this.blockedIPs.add(ip);
        console.log(`🚫 Manually blocked IP: ${ip}`);
    }

    /**
     * Unblock an IP
     */
    static unblockIP(ip: string): void {
        this.blockedIPs.delete(ip);
        const activity = this.ipActivity.get(ip);
        if (activity) {
            activity.failedLoginAttempts = 0;
            activity.suspiciousRequests = 0;
        }
        console.log(`✅ Unblocked IP: ${ip}`);
    }

    /**
     * Get all detected attacks
     */
    static getDetectedAttacks(): DetectedAttack[] {
        return [...this.detectedAttacks].sort((a, b) =>
            b.timestamp.getTime() - a.timestamp.getTime()
        );
    }

    /**
     * Get attacks by type
     */
    static getAttacksByType(type: AttackPattern['type']): DetectedAttack[] {
        return this.detectedAttacks.filter(attack => attack.type === type);
    }

    /**
     * Get attacks by IP
     */
    static getAttacksByIP(ip: string): DetectedAttack[] {
        return this.detectedAttacks.filter(attack => attack.ip === ip);
    }

    /**
     * Get all blocked IPs
     */
    static getBlockedIPs(): string[] {
        return Array.from(this.blockedIPs);
    }

    /**
     * Get IP activity
     */
    static getIPActivity(ip: string): IPActivity | null {
        return this.ipActivity.get(ip) || null;
    }

    /**
     * Get all IP activities
     */
    static getAllIPActivities(): IPActivity[] {
        return Array.from(this.ipActivity.values());
    }

    /**
     * Get attack statistics
     */
    static getAttackStatistics() {
        const stats = {
            total: this.detectedAttacks.length,
            byType: {} as Record<string, number>,
            bySeverity: {} as Record<string, number>,
            blocked: this.detectedAttacks.filter(a => a.blocked).length,
            autoBlocked: this.detectedAttacks.filter(a => a.autoBlocked).length,
            recent: this.detectedAttacks.filter(a =>
                Date.now() - a.timestamp.getTime() < 3600000 // last hour
            ).length
        };

        this.detectedAttacks.forEach(attack => {
            stats.byType[attack.type] = (stats.byType[attack.type] || 0) + 1;
            stats.bySeverity[attack.severity] = (stats.bySeverity[attack.severity] || 0) + 1;
        });

        return stats;
    }

    /**
     * Clear old data (cleanup)
     */
    static cleanup(olderThanHours: number = 24): void {
        const cutoffTime = Date.now() - (olderThanHours * 3600000);

        // Remove old attacks
        this.detectedAttacks = this.detectedAttacks.filter(
            attack => attack.timestamp.getTime() > cutoffTime
        );

        // Remove old IP activities
        for (const [ip, activity] of this.ipActivity.entries()) {
            if (activity.lastSeen.getTime() < cutoffTime) {
                this.ipActivity.delete(ip);
            }
        }

        console.log(`🧹 Cleaned up old security data (older than ${olderThanHours} hours)`);
    }

    /**
     * Reset all data (for testing)
     */
    static reset(): void {
        this.ipActivity.clear();
        this.detectedAttacks = [];
        this.blockedIPs.clear();
        console.log('🔄 Reset attack detection service');
    }
}

export default AttackDetectionService;
